import { useEffect, useState } from "react";
import {
    UsersIcon,
    ChatBubbleBottomCenterTextIcon,
    ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";

// `value` is sent as the `client_app` query param and must match the backend
// enum in app/helpers/client_app.py. The desktop_buddy label is per-facility
// branding (Genserve Buddy, NEMS Buddy, …) supplied by the parent.
const buildSources = (buddyLabel) => [
    { key: "platform", label: "Platform" },
    { key: "desktop_buddy", label: buddyLabel },
];

const StatTile = ({ icon: Icon, title, value, totalCount }) => (
    <div className="flex min-w-0 flex-col gap-5 rounded-2xl border border-[#ECEEF4] bg-white px-5 py-5 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F3EAFE]">
                <Icon className="h-5 w-5 text-[#7C3AED]" />
            </div>
            <p className="min-w-0 line-clamp-2 break-words text-sm sm:text-[15px] leading-snug font-medium text-[#4B5266]" title={title}>
                {title}
            </p>
        </div>
        <p
            className="truncate text-3xl sm:text-4xl font-extrabold leading-none tracking-tight text-[#14122B]"
            {...(totalCount != null ? { title: totalCount } : {})}
        >
            {value ?? "–"}
        </p>
    </div>
);

// Per-source KPI tiles (active sessions, chats, queries), one row per client
// app. Rendered only for facilities enrolled in
// REACT_APP_CLIENT_APP_FILTER_FACILITIES, so mounting implies the APIs may be hit.
export default function SourceMetrics({ buddyLabel }) {
    const [metricsBySource, setMetricsBySource] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const {
        selectedFacilityFilter,
        selectedStartDateFilter,
        selectedEndDateFilter,
    } = useDashboardUI();

    const sources = buildSources(buddyLabel);

    useEffect(() => {
        let cancelled = false;

        // Chats/queries come from kpi-summary and active sessions from
        // platform-usage-analytics; both narrow by client_app server-side.
        const fetchForSource = async (clientApp) => {
            const queryParams = buildAnalyticsQueryParams({
                startValue: selectedStartDateFilter,
                endValue: selectedEndDateFilter,
                facilityId: selectedFacilityFilter?.facility_number,
                extra: { client_app: clientApp },
            });
            const [kpiRes, usageRes] = await Promise.all([
                DashboardMetricsService.getKpiSummary(queryParams),
                DashboardMetricsService.getPlatformUsage(queryParams),
            ]);
            const kpi = kpiRes?.data || {};
            return {
                activeSessions: usageRes?.data?.total_active_sessions ?? null,
                chats: kpi.total_chats?.stat ?? null,
                chatsTotal: kpi.total_chats?.total_count ?? null,
                queries: kpi.total_queries?.stat ?? null,
                queriesTotal: kpi.total_queries?.total_count ?? null,
            };
        };

        const fetchAll = async () => {
            try {
                setLoading(true);
                setError(false);
                const results = await Promise.all(
                    sources.map(({ key }) => fetchForSource(key))
                );
                if (cancelled) return;
                setMetricsBySource(
                    Object.fromEntries(
                        sources.map(({ key }, i) => [key, results[i]])
                    )
                );
            } catch (err) {
                console.error("Failed to fetch source metrics", err);
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchAll();
        return () => {
            cancelled = true;
        };
    }, [selectedStartDateFilter, selectedEndDateFilter, selectedFacilityFilter]);

    if (loading) {
        return (
            <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                Loading analytics…
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-[200px] items-center justify-center text-sm text-red-500">
                Failed to load analytics
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {sources.map(({ key, label }) => {
                const m = metricsBySource[key] || {};
                return (
                    <div key={key}>
                        <h3 className="mb-3 text-base font-bold tracking-tight text-[#14122B]">
                            {label}
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <StatTile
                                icon={UsersIcon}
                                title="Total Active Session"
                                value={m.activeSessions}
                            />
                            <StatTile
                                icon={ChatBubbleBottomCenterTextIcon}
                                title="Total Chats"
                                value={m.chats}
                                totalCount={m.chatsTotal}
                            />
                            <StatTile
                                icon={ChatBubbleLeftRightIcon}
                                title="Total Queries"
                                value={m.queries}
                                totalCount={m.queriesTotal}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
